import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Play, Smartphone, CheckCircle, AlertCircle, Eye, Mail, PlusCircle,
  MapPin, Star, Globe, MessageCircle, Heart, Users, Zap,
  RefreshCw
} from 'lucide-react';

const ICON_MAP = {
  search: Eye, mail: Mail, plus: PlusCircle, map: MapPin,
  star: Star, globe: Globe, eye: Eye, message: MessageCircle,
  heart: Heart, users: Users, zap: Zap
};

const Panel = styled.div`
  display: flex; flex-direction: column; gap: 20px; padding: 20px;
`;

const DeviceSelector = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 12px; padding: 18px 20px;
`;

const SectionLabel = styled.div`
  font-size: 12px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .5px; margin-bottom: 12px;
  display: flex; align-items: center; gap: 8px;
`;

const DeviceGrid = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
`;

const DeviceBtn = styled.button`
  padding: 8px 16px; border-radius: 8px; font-weight: 700; font-size: 12px;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: all .15s;
  border: 2px solid ${p => p.$selected ? 'var(--primary)' : 'var(--card-border)'};
  background: ${p => p.$selected ? 'rgba(165,145,239,.12)' : 'var(--surface)'};
  color: ${p => p.$selected ? 'var(--primary)' : 'var(--text-secondary)'};
  &:hover { border-color: var(--primary); opacity: .9; }
`;

const OnlineDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.$on ? 'var(--success)' : 'var(--error)'};
`;

const ScriptGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px;
`;

const ScriptCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 12px; padding: 20px;
  display: flex; flex-direction: column; gap: 12px;
  opacity: ${p => p.$disabled ? 0.5 : 1};
  transition: all .2s;
  &:hover { border-color: ${p => p.$disabled ? 'var(--card-border)' : 'rgba(165,145,239,.4)'}; }
`;

const ScriptHeader = styled.div`
  display: flex; align-items: center; gap: 12px;
`;

const ScriptIcon = styled.div`
  width: 40px; height: 40px; border-radius: 10px;
  background: ${p => p.$color ? `${p.$color}20` : 'var(--surface)'};
  color: ${p => p.$color || 'var(--primary)'};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;

const ScriptInfo = styled.div`
  flex: 1;
  .name { font-size: 14px; font-weight: 700; color: var(--text-primary); }
  .desc { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
`;

const LaunchBtn = styled.button`
  padding: 10px 16px; border-radius: 8px; border: none;
  background: ${p => p.$color || 'var(--primary)'};
  color: #fff; font-weight: 700; font-size: 12px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  display: flex; align-items: center; justify-content: center; gap: 6px;
  opacity: ${p => p.disabled ? 0.4 : 1};
  transition: all .15s;
  &:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
`;

const ResultBanner = styled.div`
  padding: 12px 18px; border-radius: 10px;
  background: ${p => p.$ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)'};
  border: 1px solid ${p => p.$ok ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.25)'};
  display: flex; align-items: center; gap: 10px;
  color: ${p => p.$ok ? 'var(--success)' : 'var(--error)'};
  font-size: 13px; font-weight: 600;
`;

const EmptyMsg = styled.span`
  font-size: 12px; color: var(--text-muted);
`;

/**
 * Reusable automation panel for launching scripts on devices.
 * Props:
 *   - scriptsEndpoint: GET URL that returns { success, scripts: [...] }
 *   - launchEndpoints: { scriptId: '/api/...' } map
 *   - selectedDevice: optional pre-selected device from parent
 *   - title: panel title
 */
const ScriptAutomationPanel = ({ scriptsEndpoint, launchEndpoints, selectedDevice: parentDevice, title }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [launching, setLaunching] = useState(null);
  const [result, setResult] = useState(null);

  // Use parent device if provided
  useEffect(() => {
    if (parentDevice?.device_id) {
      setSelectedDevice(parentDevice.device_id);
    }
  }, [parentDevice]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await fetch('/api/devices');
      const data = await res.json();
      const devs = (data.devices || data || []).filter(d => d.isConnected);
      setDevices(devs);
      if (!selectedDevice && devs.length > 0) {
        setSelectedDevice(devs[0].device_id);
      }
    } catch {}
  }, [selectedDevice]);

  const fetchScripts = useCallback(async () => {
    if (!scriptsEndpoint) return;
    try {
      const res = await fetch(scriptsEndpoint);
      const data = await res.json();
      if (data.success) setScripts(data.scripts);
    } catch {}
  }, [scriptsEndpoint]);

  useEffect(() => {
    fetchDevices();
    fetchScripts();
  }, [fetchDevices, fetchScripts]);

  const launch = async (scriptId) => {
    if (!selectedDevice || launching) return;
    setLaunching(scriptId);
    setResult(null);
    try {
      const url = launchEndpoints?.[scriptId];
      if (!url) throw new Error('Endpoint non configuré pour ce script');
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: selectedDevice, mode: 'terminal' })
      });
      const data = await res.json();
      if (data.success) {
        setResult({ ok: true, msg: data.message || 'Script lancé avec succès' });
      } else {
        setResult({ ok: false, msg: data.error || 'Erreur inconnue' });
      }
    } catch (err) {
      setResult({ ok: false, msg: err.message });
    } finally {
      setLaunching(null);
    }
  };

  return (
    <Panel>
      {/* Device Selector */}
      <DeviceSelector>
        <SectionLabel>
          <Smartphone size={14} />
          {title || 'Sélectionner un device'}
        </SectionLabel>
        <DeviceGrid>
          {devices.length === 0 && <EmptyMsg>Aucun device connecté</EmptyMsg>}
          {devices.map(d => (
            <DeviceBtn key={d.device_id} $selected={selectedDevice === d.device_id}
              onClick={() => setSelectedDevice(d.device_id)}>
              <OnlineDot $on={d.isConnected} />
              {d.device_name || d.device_id}
            </DeviceBtn>
          ))}
          <DeviceBtn style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--border)' }}
            onClick={() => { fetchDevices(); fetchScripts(); }}>
            <RefreshCw size={12} /> Rafraîchir
          </DeviceBtn>
        </DeviceGrid>
      </DeviceSelector>

      {/* Script Cards */}
      <ScriptGrid>
        {scripts.map(s => {
          const Icon = ICON_MAP[s.icon] || Zap;
          return (
            <ScriptCard key={s.id} $disabled={!s.exists}>
              <ScriptHeader>
                <ScriptIcon $color={s.color}><Icon size={20} /></ScriptIcon>
                <ScriptInfo>
                  <div className="name">{s.name}</div>
                  <div className="desc">{s.description}</div>
                </ScriptInfo>
              </ScriptHeader>
              <LaunchBtn $color={s.exists ? s.color : undefined}
                disabled={!s.exists || !selectedDevice || launching === s.id}
                onClick={() => launch(s.id)}>
                {launching === s.id ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Lancement...</> :
                  <><Play size={13} /> {s.exists ? 'Lancer' : 'Script manquant'}</>}
              </LaunchBtn>
            </ScriptCard>
          );
        })}
      </ScriptGrid>

      {/* Result */}
      {result && (
        <ResultBanner $ok={result.ok}>
          {result.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {result.msg}
        </ResultBanner>
      )}
    </Panel>
  );
};

export default ScriptAutomationPanel;
